/* @jsxRuntime automatic */
/* @jsxImportSource @superweb/css */

import { useState } from "react";

import { useLocale } from "@superweb/intl";
import { cssFns } from "@superweb/css";
import { useTypo, useUiColors } from "@superweb/ui";
import { useFlag } from "@superweb/flags";

import { useAction, useQuery } from "@fleet/api";

import {
  searchParamTypes as t,
  useMakeLink,
  useNavigate,
  useSearchParams,
} from "#internal/router-new";
import {
  AppLayout,
  LinkPanel,
  NotFound,
  Offer,
  OfferDeprecated,
  PageSpinner,
} from "#internal/ui";
import { useMessage } from "#internal/intl";
import { useLogAction } from "#internal/analytics";
import { FormHeader } from "#internal/ui/form";
import {
  useErrorMessages,
  useSubmitErrorMessages,
} from "#internal/utils/validation";
import SerbiaOfferPng from "#internal/images/serbia-offer.png";
import SerbiaOfferWebp from "#internal/images/serbia-offer.webp";
import SerbiaOfferAvif from "#internal/images/serbia-offer.avif";

import { getSteps } from "../steps";

const deliveryProfessionIds = new Set([
  "cargo_courier_on_foot",
  "cargo_courier_on_bicycle",
  "cargo_courier_on_scooter",
  "cargo_courier_on_moto",
  "cargo_courier_on_car",
  "cargo_courier_on_truck",
]);

const Content = ({
  refId,
  professionId,
  sessionId,
  specification,
  proId,
  leadId,
  previousStep,
  isMultipleProfessions,
  isMultipleEmploymentTypes,
  isIframe,
  confirmationId,
}: {
  refId: string;
  professionId?: string;
  sessionId?: string;
  specification?: string;
  proId?: boolean;
  leadId?: string;
  previousStep?: "profession";
  isMultipleProfessions?: boolean;
  isMultipleEmploymentTypes?: boolean;
  isIframe: boolean;
  confirmationId?: string;
}) => {
  const logActionMultiProfessionForm = useLogAction("multi_profession_form");
  const logActionContact = useLogAction("contact");
  const locale = useLocale();
  const message = useMessage();
  const employmentTypesConfig = useEmploymentTypesConfig();
  const navigate = useNavigate();
  const errorMessages = useErrorMessages();
  const typo = useTypo();
  const uiColors = useUiColors();
  const flag = useFlag();
  const enableRemoveDeprecatedFields = flag({
    id: "27bb304b-c27d-4dbd-bd19-29a38e62254d",
    description: "Enable remove deprecated fields",
  });
  const makeLink = useMakeLink();

  const [formErrorMessage, setFormErrorMessage] = useState<string>();

  const { response } = useQuery({
    url: "/fleet-forms/selfreg/v1/park/info",
    method: "GET",
    headers: {
      language: locale.toString(),
      "x-session-id": sessionId,
    },
    query: { ref_id: refId, profession_id: professionId },
  });
  const parkInfo = response.status === 200 ? response.body : undefined;

  const submitErrorMessages = useSubmitErrorMessages(
    parkInfo?.country_code ?? "",
  );

  const professionSettings = parkInfo?.profession_settings_list.find(
    (profession) => profession.profession_id === professionId,
  );
  const employmentTypeSettingsList =
    professionSettings?.employment_type_list ?? [];

  const { stepsCount, currentStep } = getSteps({
    formRequirements: enableRemoveDeprecatedFields
      ? professionSettings?.form_requirements
      : parkInfo?.form_requirements,
    currentStep: "employment-type",
    isMultipleProfessions,
    isMultipleEmploymentTypes: true,
    isLoginWithPro: proId,
  });
  const { action: fetchSuggest, isFetching: isFetchingSuggest } = useAction({
    url: "/fleet-forms/selfreg/v1/suggest",
    method: "GET",
  });

  const { action: createProfile, isFetching: isSubmittingCreateProfile } =
    useAction({
      url: "/fleet-forms/selfreg/v1/submit",
      method: "POST",
    });

  const searchParams = new URLSearchParams({
    ref_id: refId,
    ...(sessionId ? { sessionId } : undefined),
    ...(specification ? { specification } : undefined),
    ...(professionId ? { profession_id: professionId } : undefined),
    ...(leadId ? { lead_id: leadId } : undefined),
    ...(proId ? { pro_id: proId.toString() } : undefined),
    ...(isMultipleProfessions
      ? { is_multiple_professions: "true" }
      : undefined),
    ...(isMultipleEmploymentTypes
      ? { is_multiple_employment_types: "true" }
      : undefined),
    ...(confirmationId ? { confirmation_id: confirmationId } : undefined),
  });

  if (!parkInfo) {
    return <NotFound />;
  }

  if (isFetchingSuggest || isSubmittingCreateProfile) {
    return <PageSpinner />;
  }
  const isDelivery =
    !isMultipleProfessions &&
    deliveryProfessionIds.has(
      parkInfo.profession_settings_list[0]?.profession_id ?? "",
    );
  const offerType = isMultipleProfessions
    ? "multiple"
    : isDelivery
      ? "delivery"
      : "taxi";

  const enableNewOffer = flag({
    id: "27e3642e-9510-45a2-8dd7-69ddd4e47936",
    description: "Enable new offer",
  });
  const enableSerbiaOfferImage =
    parkInfo.park_id === "1a1d6c5f1ad7423481a1bc8a8c5d09db";

  return (
    <AppLayout
      title={
        isIframe
          ? undefined
          : message({
              id: "019a9c26-1400-769b-a014-3101a05d9f63",
              context: "Employment type form. Title",
              default: "Employment type",
            })
      }
      partnerName={parkInfo.partner_name ?? ""}
      content={
        <div
          css={{
            display: "grid",
            gridTemplateRows: "auto 1fr auto",
            height: "100%",
            boxSizing: "border-box",
          }}
        >
          <FormHeader
            title={message({
              id: "019a9c2c-4c2f-762b-bfc1-67d10f4f754e",
              context: "Employment type form. Title",
              default: "Select employment type ",
            })}
            step={
              currentStep
                ? { current: currentStep, total: stepsCount }
                : undefined
            }
            {...(previousStep || isMultipleProfessions
              ? {
                  previousStep: makeLink(
                    `/${previousStep ?? "profession"}?${searchParams}`,
                  ),
                }
              : {})}
          />
          <div
            css={{
              alignSelf: "start",
              display: "flex",
              flexWrap: "wrap",
              alignItems: "start",
              ...cssFns.gap("16px"),
              ...cssFns.padding("16px"),
            }}
          >
            {employmentTypeSettingsList.map((employmentType) => {
              const employmentTypeId = employmentType.employment_type;

              const needCreateProfile =
                employmentTypeId === "park_employee" && (proId || leadId);

              const searchParams = new URLSearchParams({
                ref_id: refId,
                ...(sessionId ? { sessionId } : undefined),
                ...(specification ? { specification } : undefined),
                ...(professionId ? { profession_id: professionId } : undefined),
                ...(proId ? { pro_id: proId.toString() } : undefined),
                ...(leadId ? { lead_id: leadId } : undefined),
                employment_type_id: employmentTypeId,
                ...(!needCreateProfile
                  ? { previous_step: "employment-type" }
                  : undefined),
                ...(isMultipleProfessions
                  ? { is_multiple_professions: "true" }
                  : undefined),
                is_multiple_employment_types: "true",
                ...(confirmationId
                  ? { confirmation_id: confirmationId }
                  : undefined),
              });

              const { nextStep } = getSteps({
                formRequirements: enableRemoveDeprecatedFields
                  ? professionSettings?.form_requirements
                  : professionSettings?.form_requirements ??
                    parkInfo.form_requirements,
                currentStep: "employment-type",
                employmentType: employmentTypeId,
                isMultipleProfessions,
                isMultipleEmploymentTypes: true,
                isLoginWithPro: proId,
              });

              return (
                <LinkPanel
                  key={employmentTypeId}
                  onPress={() => {
                    logActionMultiProfessionForm("employment_type_click", {
                      params: { employment_type: employmentTypeId },
                    });

                    if (needCreateProfile) {
                      setFormErrorMessage(undefined);

                      fetchSuggest(
                        {
                          headers: {
                            language: locale.toString(),
                          },
                          query: {
                            ref_id: refId,
                            employment_type: "park_employee",
                            ...(leadId ? { lead_id: leadId } : undefined),
                            apply_auth: proId,
                          },
                        },
                        {
                          onResponse: (suggestRes) => {
                            if (suggestRes.status !== 200) {
                              setFormErrorMessage(errorMessages.default);
                              return;
                            }

                            const suggest = suggestRes.body;
                            const hasAllSuggestFields =
                              suggest.phone &&
                              suggest.name?.first &&
                              suggest.name.last;

                            if (!hasAllSuggestFields || !sessionId) {
                              setFormErrorMessage(errorMessages.default);
                              return;
                            }

                            createProfile(
                              {
                                headers: {
                                  "x-submission-id": sessionId,
                                  language: locale.toString(),
                                  "content-type": "application/json",
                                },
                                query: {
                                  ref_id: refId,
                                  apply_auth: proId,
                                },
                                body: {
                                  personal: {
                                    first_name: suggest.name!.first!,
                                    last_name: suggest.name!.last!,
                                    phone: suggest.phone!,
                                    agree_processing_data: true,
                                    agree_receive_info: true,
                                    profession_id: professionId,
                                    employment_type: employmentTypeId,
                                    confirmation_id: confirmationId
                                      ? confirmationId
                                      : undefined,
                                  },
                                },
                              },
                              {
                                onResponse: (submitRes) => {
                                  if (submitRes.status === 204) {
                                    navigate(`/${nextStep}?${searchParams}`);
                                  } else {
                                    setFormErrorMessage(
                                      submitErrorMessages[
                                        submitRes.body.code
                                      ] ?? errorMessages.default,
                                    );
                                  }
                                },
                              },
                            );
                          },
                        },
                      );
                    }
                  }}
                  icon={employmentTypesConfig[employmentTypeId].icon}
                  label={employmentTypesConfig[employmentTypeId].label}
                  description={
                    employmentTypesConfig[employmentTypeId].description
                  }
                  link={
                    needCreateProfile
                      ? undefined
                      : makeLink(`/${nextStep}?${searchParams}`)
                  }
                />
              );
            })}
          </div>
          {formErrorMessage && (
            <span
              role="alert"
              aria-live="assertive"
              css={{
                ...typo({
                  level: "caption1",
                  weight: "regular",
                  density: "tight",
                }),
                textAlign: "center",
                ...cssFns.padding("12px", "16px"),
                color: uiColors.statuses.danger,
              }}
            >
              {formErrorMessage}
            </span>
          )}
        </div>
      }
      offer={
        isIframe ? undefined : enableNewOffer ? (
          <Offer
            offerText={parkInfo.promo_text}
            image={
              enableSerbiaOfferImage
                ? {
                    avif: SerbiaOfferAvif,
                    png: SerbiaOfferPng,
                    webp: SerbiaOfferWebp,
                    height: "130px",
                    width: "178px",
                    padding: "24px",
                  }
                : undefined
            }
            type={offerType}
            onPhonePress={() =>
              logActionContact("phone_click_v2", {
                params: { stage: "register" },
              })
            }
            onMessengerPress={() =>
              logActionContact("messenger_click_v2", {
                params: { stage: "register" },
              })
            }
            phone={parkInfo.phone}
            messageLink={parkInfo.message_link}
          />
        ) : (
          <OfferDeprecated
            parkId={parkInfo.park_id}
            offerText={parkInfo.promo_text}
            isDelivery={specification === "delivery"}
            onPhonePress={() =>
              logActionContact("phone_click_v2", {
                params: { stage: "details" },
              })
            }
            onMessengerPress={() =>
              logActionContact("messenger_click_v2", {
                params: { stage: "details" },
              })
            }
            phone={parkInfo.phone}
            messageLink={parkInfo.message_link}
          />
        )
      }
    />
  );
};

export const EmploymentTypeForm = ({ isIframe }: { isIframe: boolean }) => {
  const [
    {
      ref_id,
      sessionId,
      specification,
      profession_id,
      pro_id: proId,
      lead_id: leadId,
      previous_step: previousStep,
      is_multiple_professions: isMultipleProfessions,
      is_multiple_employment_types: isMultipleEmploymentTypes,
      confirmation_id: confirmationId,
    },
  ] = useSearchParams({
    ref_id: t.string,
    sessionId: t.string,
    specification: t.string,
    profession_id: t.string,
    pro_id: t.boolean,
    lead_id: t.string,
    previous_step: t.enumString(["profession"] as const),
    is_multiple_professions: t.boolean,
    is_multiple_employment_types: t.boolean,
    confirmation_id: t.string,
  });

  if (!ref_id) {
    return <NotFound />;
  }

  return (
    <Content
      refId={ref_id}
      sessionId={sessionId}
      specification={specification}
      professionId={profession_id}
      proId={proId}
      leadId={leadId}
      previousStep={previousStep}
      isMultipleProfessions={isMultipleProfessions}
      isMultipleEmploymentTypes={isMultipleEmploymentTypes}
      isIframe={isIframe}
      confirmationId={confirmationId}
    />
  );
};

const useEmploymentTypesConfig = () => {
  const message = useMessage();

  return {
    park_employee: {
      icon: "park-employee",
      label: message({
        id: "019a9c11-40ee-77cf-961e-f0ef11fab120",
        context: "Employment type form. Park employee employment type label",
        default: "Fleet contractor",
      }),
      description: message({
        id: "019a9c55-948a-7619-a0eb-8d07ed8f00ad",
        context:
          "Employment type form. Park employee employment type description",
        default:
          "You use the service through one of our partners. They handle all the paperwork, reporting, and payouts. It's simple: you get trip requests and earn money, and the partner charges a fee",
      }),
    },
    selfemployed: {
      icon: "selfemployed",
      label: message({
        id: "019a9c11-963b-753c-b763-0b5c4f106918",
        context: "Employment type form. Selfemployed employment type label",
        default: "Fleet self-employed",
      }),
      description: message({
        id: "019a9c55-9ecc-719a-b971-782a89f34474",
        context:
          "Employment type form. Selfemployed employment type description",
        default:
          "You're registered as self-employed, but you use the service through one of our partners. The partner helps with trips and payouts, and taxes are paid automatically through My Taxes",
      }),
    },
    individual_entrepreneur: {
      icon: "individual-entrepreneur",
      label: message({
        id: "019a9c11-f761-767c-8c5a-7f6d0dcb214b",
        context:
          "Employment type form. Individual entrepreneur employment type label",
        default: "Individual entrepreneur",
      }),
      description: message({
        id: "019a9c55-bd90-72be-a76f-b0ff65fbdac9",
        context:
          "Employment type form. Individual entrepreneur employment type description",
        default:
          "You're registered as an Individual Entrepreneur, but you use the service through one of our partners. Earnings are sent to your account, and you manage your own taxes and documents. This option gives the most independence",
      }),
    },
  } as const;
};
